/**
 * 性能优化工具集
 * 提供防抖、节流、RAF优化等工具函数
 */

/**
 * 防抖函数 - 延迟执行，多次触发只执行最后一次
 * @param {Function} fn - 要执行的函数
 * @param {number} delay - 延迟时间（毫秒）
 * @param {boolean} immediate - 是否立即执行第一次
 * @returns {Function}
 */
export function debounce(fn, delay = 150, immediate = false) {
  let timer = null
  return function(...args) {
    const context = this
    
    if (timer) clearTimeout(timer)
    
    if (immediate && !timer) {
      fn.apply(context, args)
    }
    
    timer = setTimeout(() => {
      if (!immediate) {
        fn.apply(context, args)
      }
      timer = null
    }, delay)
  }
}

/**
 * 节流函数 - 固定时间间隔执行
 * @param {Function} fn - 要执行的函数
 * @param {number} delay - 时间间隔（毫秒）
 * @param {Object} options - 配置选项
 * @returns {Function}
 */
export function throttle(fn, delay = 150, options = {}) {
  let timer = null
  let lastTime = 0
  const { leading = true, trailing = true } = options
  
  return function(...args) {
    const context = this
    const now = Date.now()
    
    // 首次不执行
    if (!leading && !lastTime) {
      lastTime = now
    }
    
    const remaining = delay - (now - lastTime)
    
    if (remaining <= 0 || remaining > delay) {
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      lastTime = now
      fn.apply(context, args)
    } else if (!timer && trailing) {
      timer = setTimeout(() => {
        lastTime = leading ? Date.now() : 0
        timer = null
        fn.apply(context, args)
      }, remaining)
    }
  }
}

/**
 * 使用 requestAnimationFrame 优化的节流
 * 适用于滚动、拖拽等高频事件
 * @param {Function} fn - 要执行的函数
 * @returns {Function}
 */
export function rafThrottle(fn) {
  let rafId = null
  let lastArgs = null
  
  return function(...args) {
    lastArgs = args
    
    if (rafId) return
    
    rafId = requestAnimationFrame(() => {
      fn.apply(this, lastArgs)
      rafId = null
    })
  }
}

/**
 * 延迟执行（Promise 版本）
 * @param {number} ms - 延迟时间（毫秒）
 * @returns {Promise}
 */
export function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * 批量执行任务（分片执行，避免阻塞）
 * @param {Array} tasks - 任务数组
 * @param {number} chunkSize - 每批执行的任务数
 * @param {number} delay - 每批之间的延迟（毫秒）
 * @returns {Promise}
 */
export async function batchExecute(tasks, chunkSize = 10, delay = 0) {
  const results = []
  
  for (let i = 0; i < tasks.length; i += chunkSize) {
    const chunk = tasks.slice(i, i + chunkSize)
    const chunkResults = await Promise.all(
      chunk.map(task => typeof task === 'function' ? task() : task)
    )
    results.push(...chunkResults)
    
    if (delay > 0 && i + chunkSize < tasks.length) {
      await sleep(delay)
    }
  }
  
  return results
}

/**
 * 创建缓存函数（记忆化）
 * @param {Function} fn - 要缓存的函数
 * @param {Function} keyGenerator - 缓存键生成函数
 * @returns {Function}
 */
export function memoize(fn, keyGenerator = (...args) => JSON.stringify(args)) {
  const cache = new Map()
  
  return function(...args) {
    const key = keyGenerator(...args)
    
    if (cache.has(key)) {
      return cache.get(key)
    }
    
    const result = fn.apply(this, args)
    cache.set(key, result)
    
    return result
  }
}

/**
 * 懒加载图片
 * @param {HTMLImageElement} img - 图片元素
 * @param {string} src - 图片地址
 * @param {Object} options - 配置选项
 */
export function lazyLoadImage(img, src, options = {}) {
  const { threshold = 0.1, rootMargin = '50px' } = options
  
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          img.src = src
          observer.unobserve(img)
        }
      })
    }, { threshold, rootMargin })
    
    observer.observe(img)
  } else {
    // 降级方案
    img.src = src
  }
}

/**
 * 检测设备性能等级
 * @returns {'high' | 'medium' | 'low'}
 */
export function detectPerformanceLevel() {
  // 检测硬件并发数（CPU核心数）
  const cores = navigator.hardwareConcurrency || 2
  
  // 检测内存（如果支持）
  const memory = navigator.deviceMemory || 4
  
  // 检测连接速度（如果支持）
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
  const effectiveType = connection?.effectiveType || '4g'
  
  // 综合评分
  let score = 0
  
  if (cores >= 8) score += 3
  else if (cores >= 4) score += 2
  else score += 1
  
  if (memory >= 8) score += 3
  else if (memory >= 4) score += 2
  else score += 1
  
  if (effectiveType === '4g') score += 2
  else if (effectiveType === '3g') score += 1
  
  if (score >= 7) return 'high'
  if (score >= 4) return 'medium'
  return 'low'
}

/**
 * 空闲时执行任务
 * @param {Function} fn - 要执行的函数
 * @param {Object} options - 配置选项
 */
export function runWhenIdle(fn, options = {}) {
  const { timeout = 2000 } = options
  
  if ('requestIdleCallback' in window) {
    requestIdleCallback(fn, { timeout })
  } else {
    setTimeout(fn, 1)
  }
}

/**
 * 优化的事件监听器（自动使用 passive）
 * @param {Element} element - DOM 元素
 * @param {string} event - 事件名称
 * @param {Function} handler - 事件处理函数
 * @param {Object} options - 配置选项
 */
export function addOptimizedListener(element, event, handler, options = {}) {
  const passiveEvents = ['scroll', 'wheel', 'touchstart', 'touchmove', 'touchend']
  const shouldBePassive = passiveEvents.includes(event)
  
  const finalOptions = {
    ...options,
    passive: shouldBePassive && options.passive !== false
  }
  
  element.addEventListener(event, handler, finalOptions)
  
  // 返回清理函数
  return () => element.removeEventListener(event, handler, finalOptions)
}

/**
 * 预加载资源
 * @param {string} url - 资源地址
 * @param {string} type - 资源类型 ('image' | 'script' | 'style')
 * @returns {Promise}
 */
export function preloadResource(url, type = 'image') {
  return new Promise((resolve, reject) => {
    if (type === 'image') {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = reject
      img.src = url
    } else if (type === 'script') {
      const script = document.createElement('script')
      script.onload = () => resolve(script)
      script.onerror = reject
      script.src = url
      document.head.appendChild(script)
    } else if (type === 'style') {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.onload = () => resolve(link)
      link.onerror = reject
      link.href = url
      document.head.appendChild(link)
    }
  })
}

export default {
  debounce,
  throttle,
  rafThrottle,
  sleep,
  batchExecute,
  memoize,
  lazyLoadImage,
  detectPerformanceLevel,
  runWhenIdle,
  addOptimizedListener,
  preloadResource
}

